/**
 * Typed API client for the Alkisen backend (Fastify).
 *
 * - Uses `fetch` with credentials and dev-headers in development
 * - Throws ApiError on non-2xx with structured fields
 * - Resolves to `data` directly (already JSON.parsed)
 */
import { getClientId } from '@/lib/client-id';

export interface ApiErrorBody {
  code: string;
  message: string;
  details?: unknown;
}

export class ApiError extends Error {
  status: number;
  code: string;
  details?: unknown;

  constructor(status: number, body: ApiErrorBody) {
    super(body.message);
    this.status = status;
    this.code = body.code;
    this.details = body.details;
    this.name = 'ApiError';
  }
}

const DEV_HEADERS = {
  'x-user-id': import.meta.env.VITE_DEV_USER_ID ?? 'dev-user',
  'x-user-email': import.meta.env.VITE_DEV_USER_EMAIL ?? 'dev@vin.invested.no',
  'x-user-role': import.meta.env.VITE_DEV_USER_ROLE ?? 'ADMIN',
  'x-user-tenant-id': import.meta.env.VITE_DEV_TENANT_ID ?? 'vin',
};

const API_BASE = import.meta.env.VITE_API_BASE_URL || '';

interface FetchOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined | null>;
}

export async function api<T>(path: string, opts: FetchOptions = {}): Promise<T> {
  const url = new URL(`${API_BASE}${path}`, window.location.origin);
  if (opts.query) {
    for (const [k, v] of Object.entries(opts.query)) {
      if (v !== undefined && v !== null && v !== '') url.searchParams.set(k, String(v));
    }
  }

  const headers: Record<string, string> = {
    Accept: 'application/json',
    'x-alkisen-client-id': getClientId(),
    ...(opts.body ? { 'Content-Type': 'application/json' } : {}),
    ...(import.meta.env.DEV ? DEV_HEADERS : {}),
    ...((opts.headers as Record<string, string>) ?? {}),
  };

  const res = await fetch(url.toString(), {
    method: opts.method ?? 'GET',
    credentials: 'include',
    headers,
    body: opts.body ? JSON.stringify(opts.body) : undefined,
    signal: opts.signal,
  });

  if (res.status === 204) return undefined as T;

  const text = await res.text();
  const json: unknown = text ? safeParse(text) : null;

  if (!res.ok) {
    const body = (json as { error?: ApiErrorBody })?.error ?? {
      code: 'HTTP_ERROR',
      message: fallbackHttpMessage(res.status, res.statusText),
    };
    throw new ApiError(res.status, body);
  }
  return json as T;
}

function fallbackHttpMessage(status: number, statusText: string): string {
  if (status === 503) {
    return 'Tjenesten er midlertidig utilgjengelig. Prøv igjen om litt; hvis det skjer igjen, send en bugrapport.';
  }
  if (status === 502 || status === 504) {
    return 'En ekstern tjeneste svarte ikke. Prøv igjen, eller send en bugrapport hvis det gjentar seg.';
  }
  return statusText || `HTTP ${status}`;
}

function safeParse(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

export const apiBase = API_BASE;
